import * as AudioBackup from "../interfaces/IAudioBackup";
import * as AudioFormat from "../interfaces/IAudioFormat";
import { IndexeddbBackupKeys } from "./telemetry/IndexeddbBackupKeys";
import { LogEvents } from "../../utils/LogEvents";
import { AudioBackupMethodStates, AudioBackupLogError } from "./telemetry/ErrorHelper";
import { AudioBackupError, AudioBackupErrors } from "./AudioBackupErrors";
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { LogError } from "../../utils/ErrorTelemetry";
import { ErrorStringFromException } from "../../utils/utils";

type StoreItemType = AudioFormat.IFormatProperties | ArrayBuffer;

export class IndexeddbAudioBackup implements AudioBackup.IAudioBackup {
    private readonly dbName: string = "officeTranscriptionAudioBackup";
    private readonly storeNameChunks: string = "audioChunks";
    private readonly storeNameFormat: string = "audioFormat";
    private readonly dbVersion: number = 1;
    private idbFactory: IDBFactory;
    private db: IDBDatabase | null;
    private isClearing: boolean;

    constructor (indexedDBIn: IDBFactory) {
        this.idbFactory = indexedDBIn;
        this.db = null;
        this.isClearing = false;
    }

    public async Initialize(): Promise<void> {
        LogEvents.IndexeddbBackup.Start([{key: IndexeddbBackupKeys.Initialize, value: AudioBackupMethodStates.called}]);
        return new Promise<void>((resolve, reject) => {
            try {
                const request: IDBOpenDBRequest = this.idbFactory.open(this.dbName, this.dbVersion);
                request.onblocked = () => LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.Initialize, value: AudioBackupMethodStates.blocked}]);
                request.onerror = () => {
                    let failureMessage = IndexeddbBackupKeys.Initialize + " failed. Reason: Failed to open db because an error occured. Error code: " + request.error;
                    LogError(LogEventNames.Warning, failureMessage);
                    reject(new AudioBackupError(AudioBackupErrors.IndexedDBOpenError, failureMessage));
                }
                request.onupgradeneeded = () => {
                    LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.Initialize, value: AudioBackupMethodStates.upgradeneeded}]);
                    request.result.createObjectStore(this.storeNameChunks, { autoIncrement: true});
                    request.result.createObjectStore(this.storeNameFormat, { autoIncrement: true});
                }
                request.onsuccess = () => {
                    LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.Initialize, value: AudioBackupMethodStates.success}]);
                    this.db = request.result;
                    resolve();
                }
            } catch (e) {
                LogError(LogEventNames.Warning, IndexeddbBackupKeys.Initialize + " failed. Reason: Exception opening db. Details: " + ErrorStringFromException(e));
                reject();
            }
        });
    }

    public IsInitializedSuccessfully(): boolean {
        return (this.db != null);
    }

    public Close(): void {
        if (this.db != null) {
            LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.Close, value: AudioBackupMethodStates.called}]);
            this.db.close();
            this.db = null;
            LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.Close, value: AudioBackupMethodStates.success}]);
        }

        if (!this.isClearing) {
            LogEvents.IndexeddbBackup.End();
        }
    }

    public FetchBackupState(): Promise<AudioBackup.BackupStates> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchBackupState, value: AudioBackupMethodStates.called}]);
        return new Promise<AudioBackup.BackupStates>(async (resolve) => {
            if (this.db == null) {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchBackupState, value: AudioBackupMethodStates.success}]);
                resolve(AudioBackup.BackupStates.None);
                return;
            }

            try {
                const countOfItemsInFormat: number = await this.FetchCountOfItemsFromStore(this.storeNameFormat);
                const countOfItemsInChunks: number = await this.FetchCountOfItemsFromStore(this.storeNameChunks);
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchBackupState, value: AudioBackupMethodStates.success}]);
                if (countOfItemsInFormat == 1 && countOfItemsInChunks > 0) {
                    resolve(AudioBackup.BackupStates.Active);
                } else if (countOfItemsInFormat == 0 && countOfItemsInChunks == 0) {
                    resolve(AudioBackup.BackupStates.None);
                } else {
                    throw 'Invalid indexeddb backup state detected';
                }
            } catch(error: any) {
                AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchBackupState, error);

                // We resolve instead of rejecting because we expect this function to never throw and always return None/Active/Invalid.
                // The caller can then determine whether to clear the indexeddb or not in the Invalid case - else if we throw the caller may simply navigate
                // to an error page that makes it seem irrecoverable.
                resolve(AudioBackup.BackupStates.Invalid);
            }
        });
    }

    public ClearBackup(): Promise<void> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.ClearBackup, value: AudioBackupMethodStates.called}]);
        this.isClearing = true;
        this.Close();
        this.isClearing = false;
        return new Promise<void>((resolve, reject) => {
            const request: IDBOpenDBRequest = this.idbFactory.deleteDatabase(this.dbName);
            request.onblocked = () => {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.ClearBackup, value: AudioBackupMethodStates.blocked}]); }
            request.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.ClearBackup,
                                            "Failed to close/clear db because an error occured. Error code: " + request.error)); }
            request.onsuccess = () => {
                if (request.result != null) {
                    reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.ClearBackup,
                        "Failed to close/clear db, though we received an onsuccess. Error code: " + request.error));
                }

                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.ClearBackup, value: AudioBackupMethodStates.success}]);
                LogEvents.IndexeddbBackup.End();
                resolve();
            }
        });
    }

    public BackupFormatProperties(props: AudioFormat.IFormatProperties): Promise<void> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupFormatProperties, value: AudioBackupMethodStates.called}]);
        return new Promise<void>(async (resolve, reject) => {
            if (this.db == null) {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupFormatProperties,
                                            "Backup failed because of an uninitialized database"));
            }

            try {
                const countOfItemsInStore: number = await this.FetchCountOfItemsFromStore(this.storeNameFormat);
                if (countOfItemsInStore != 0) {
                    reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupFormatProperties,
                        "Format store has " + countOfItemsInStore + " entries. It should have no entries"));
                }

                await this.BackupItemToStore(this.storeNameFormat, props);
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupFormatProperties, value: AudioBackupMethodStates.success}]);
                resolve();
            } catch (error: any) {
                AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupFormatProperties, error);
                reject(error);
            }
        });
    }

    public FetchFormatProperties(): Promise<AudioFormat.IFormatProperties> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchFormatProperties, value: AudioBackupMethodStates.called}]);
        return new Promise<AudioFormat.IFormatProperties>(async (resolve, reject) => {
            if (this.db == null) {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchFormatProperties,
                                            "Fetch failed because of an uninitialized database"));
            }

            try {
                const formatProperties: AudioFormat.IFormatProperties[] = <AudioFormat.IFormatProperties[]>(await this.FetchItemsFromStore(this.storeNameFormat));
                if (formatProperties.length != 1) {
                    reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchFormatProperties,
                        "Format store has " + formatProperties.length + " entries. It should have one entry"));
                }

                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchFormatProperties, value: AudioBackupMethodStates.success}]);
                resolve(formatProperties[0]);
            } catch (error: any) {
                AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchFormatProperties, error);
                reject(error);
            }
        });
    }

    public BackupChunk(chunk: ArrayBuffer): Promise<void> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupChunk, value: AudioBackupMethodStates.called}]);
        return new Promise<void>(async (resolve, reject) => {
            if (this.db == null) {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupChunk,
                                            "Backup failed because of an uninitialized database"));
            }

            try {
                await this.BackupItemToStore(this.storeNameChunks, chunk);
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupChunk, value: AudioBackupMethodStates.success}]);
                resolve();
            } catch (error: any) {
                AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupChunk, error);
                reject(error);
            }
        });
    }

    public FetchChunks(): Promise<ArrayBuffer[]> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchChunks, value: AudioBackupMethodStates.called}]);
        return new Promise<ArrayBuffer[]>(async (resolve, reject) => {
            if (this.db == null) {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchChunks,
                    "Fetch failed because of an uninitialized database"));
            }

            try {
                const buffers: ArrayBuffer[] = <ArrayBuffer[]>(await this.FetchItemsFromStore(this.storeNameChunks));
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchChunks, value: AudioBackupMethodStates.success}]);
                resolve(buffers);
            } catch (error: any) {
                AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchChunks, error);
                reject(error);
            }
        });
    }

    private FetchCountOfItemsFromStore(storeName: string): Promise<number> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchCountOfItemsFromStore, value: AudioBackupMethodStates.called}]);
        return new Promise<number>((resolve, reject) => {
            const transaction: IDBTransaction = this.db!.transaction(storeName, "readonly");
            const objectStore: IDBObjectStore = transaction.objectStore(storeName);
            const countRequest: IDBRequest = objectStore.count();
            let count: number = -1;
            countRequest.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchCountOfItemsFromStore,
                    "Failed to count items in dbStore" + storeName + ", because an error occured. Error code: " + countRequest.error));
            }

            countRequest.onsuccess = () => {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchCountOfItemsFromStore, value: AudioBackupMethodStates.success}]);
                count = countRequest.result;
            }

            transaction.onabort = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchCountOfItemsFromStore,
                    "Failed to count items in dbStore" + storeName + ", because transaction was aborted"));
            }

            transaction.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchCountOfItemsFromStore,
                    "Failed to count items in dbStore" + storeName + ", because a transaction error occured. Error code: " + transaction.error));
            }

            transaction.oncomplete = () => {
                if (count == -1) {
                    reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchCountOfItemsFromStore,
                        "Failed to count items in dbStore" + storeName + ", even after transaction completed."));
                }

                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchCountOfItemsFromStore, value: AudioBackupMethodStates.complete}]);
                resolve(count);
            }
        });
    }

    private BackupItemToStore(storeName: string, item: StoreItemType): Promise<void> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupItemToStore, value: AudioBackupMethodStates.called}]);
        return new Promise<void>((resolve, reject) => {
            const transaction: IDBTransaction = this.db!.transaction(storeName, 'readwrite');
            transaction.onabort = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupItemToStore,
                    `IndexedDB add transaction failed with message: ${transaction.error!.name}`));
            };

            transaction.oncomplete = () => {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupItemToStore, value: AudioBackupMethodStates.complete}]);
                resolve();
            }

            transaction.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupItemToStore,
                    "Failed to backup item to dbStore" + storeName + ", because a transaction error occured. Error code: " + transaction.error));
            }

            const objectStore: IDBObjectStore = transaction.objectStore(storeName);

            const validKeyRequest: IDBRequest = objectStore.add(item);
            validKeyRequest.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.BackupItemToStore,
                    "Failed to BackupItemToStore in " + storeName + " because an error occured. Error code: " + validKeyRequest.error)); }
            validKeyRequest.onsuccess = () => {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.BackupItemToStore, value: AudioBackupMethodStates.success}]);
            }
        });
    }

    private FetchItemsFromStore(storeName: string): Promise<Array<StoreItemType>> {
        LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchItemsFromStore, value: AudioBackupMethodStates.called}]);
        return new Promise<Array<StoreItemType>>((resolve, reject) => {
            let items: Array<StoreItemType> = []
            const transaction: IDBTransaction = this.db!.transaction(storeName, 'readonly');
            const objectStore: IDBObjectStore = transaction.objectStore(storeName);

            const cursorRequest: IDBRequest = objectStore.openCursor();
            cursorRequest.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchItemsFromStore,
                    "Failed to FetchItemsFromStore in " + storeName + " because an error occured. Error code: " + cursorRequest.error)); }
            cursorRequest.onsuccess = (ev: Event) => {
                const cursor: IDBCursorWithValue = (<IDBRequest>(ev.target)).result;
                if (cursor == null || cursor.value == null) {
                    LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchItemsFromStore, value: AudioBackupMethodStates.success}]);
                } else {
                    LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchItemsFromStore, value: AudioBackupMethodStates.continue}]);
                    items.push(cursor.value);
                    cursor.continue();
                }
            }

            transaction.onabort = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchItemsFromStore,
                    "Failed to fetch items from dbStore" + storeName + ", because transaction was aborted"));
            }

            transaction.onerror = () => {
                reject(AudioBackupLogError(LogEvents.IndexeddbBackup, IndexeddbBackupKeys.FetchItemsFromStore,
                    "Failed to fetch items from dbStore" + storeName + ", because a transaction error occured. Error code: " + transaction.error));
            }

            transaction.oncomplete = () => {
                LogEvents.IndexeddbBackup.AddData([{key: IndexeddbBackupKeys.FetchItemsFromStore, value: AudioBackupMethodStates.complete}]);
                resolve(items);
            }
        });
    }
};